import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import api from "../services/api";
import { Button } from "./ui/Button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { FormTemplate } from "../types/forms";
import { getForms } from "../services/forms";

interface SendFormModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  submitLabel?: string;
  endpoint?: string;
  openResponseInNewTab?: boolean;
  redirectOnSuccess?: boolean;
  useApiReturnedUrl?: boolean;
  openEncodedFormRouteInNewTab?: boolean;
}

type Customer = {
  id: number;
  name: string;
  email?: string;
};

const SendFormModal = ({
  open,
  onClose,
  title = "Send Form to Customer",
  submitLabel = "Send Form",
  endpoint = "/admin/consultations",
  openResponseInNewTab = false,
  redirectOnSuccess = false,
  useApiReturnedUrl = false,
  openEncodedFormRouteInNewTab = false,
}: SendFormModalProps) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [forms, setForms] = useState<FormTemplate[]>([]);

  const [selectedCustomerId, setSelectedCustomerId] = useState<string>();
  const [selectedFormId, setSelectedFormId] = useState<string>();

  const [customerSearch, setCustomerSearch] = useState("");
  const [formSearch, setFormSearch] = useState("");

  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);

  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!selectedCustomerId || !selectedFormId) return;

    try {
      setSending(true);

      const response = await api.post(endpoint, {
        customer_id: Number(selectedCustomerId),
        form_id: Number(selectedFormId),
      });

      if (openEncodedFormRouteInNewTab) {
        const responseConsultationId =
          response?.data?.data?.id || response?.data?.id;

        if (responseConsultationId) {
          const encodedId = window.btoa(String(responseConsultationId));
          window.open(
            `/forms/consultation/${encodedId}`,
            "_blank",
            "noopener,noreferrer",
          );
          return;
        }
      }

      if (useApiReturnedUrl) {
        const apiUrl = response?.data?.url;
        if (apiUrl && typeof apiUrl === "string") {
          window.location.href = apiUrl;
          return;
        }
      }

      if (openResponseInNewTab) {
        const consultationResponseId =
          response?.data?.data?.id || response?.data?.id;
        const customerIdFromResponse =
          response?.data?.data?.user_id || response?.data?.user_id;
        if (consultationResponseId) {
          window.open(
            `/consultation-response/${consultationResponseId}${
              customerIdFromResponse
                ? `?customerId=${customerIdFromResponse}`
                : ""
            }`,
            "_blank",
            "noopener,noreferrer",
          );
        }
      }

      if (redirectOnSuccess) {
        const consultationResponseId =
          response?.data?.data?.id || response?.data?.id;
        const customerIdFromResponse =
          response?.data?.data?.user_id || response?.data?.user_id;
        if (consultationResponseId) {
          window.location.href = `/consultation-response/${consultationResponseId}${
            customerIdFromResponse ? `?customerId=${customerIdFromResponse}` : ""
          }`;
          return;
        }
      }

      // Optional: success feedback
      // toast.success("Form sent successfully");

      onClose();
    } catch (error) {
      console.error("Failed to send consultation form", error);
      // Optional: toast.error("Failed to send form");
    } finally {
      setSending(false);
    }
  };

  /* ----------------------------- Fetch data ----------------------------- */

  useEffect(() => {
    if (!open) return;

    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const res = await api.get("/admin/customers?page=1&per_page=1000");

        // EXACT path from the API response
        const customersArray = res.data?.data?.data ?? [];

        setCustomers(customersArray);
      } finally {
        setLoadingCustomers(false);
      }
    };

    const fetchForms = async () => {
      setLoadingForms(true);
      try {
        const data = await getForms();
        setForms(data);
      } finally {
        setLoadingForms(false);
      }
    };

    fetchCustomers();
    fetchForms();
  }, [open]);

  /* ------------------------------ Filters ------------------------------ */

  const filteredCustomers = useMemo(() => {
    return customers.filter((c) =>
      c.name.toLowerCase().includes(customerSearch.toLowerCase()),
    );
  }, [customers, customerSearch]);

  const filteredForms = useMemo(() => {
    return forms.filter((f) =>
      f.title.toLowerCase().includes(formSearch.toLowerCase()),
    );
  }, [forms, formSearch]);

  const canSend = !!selectedCustomerId && !!selectedFormId;

  if (!open) return null;

  /* -------------------------------- UI -------------------------------- */

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl border border-[#E7E4F8]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-[#402B69]">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-[#6E3FA9] hover:bg-[#F3EEFF]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5">
          {/* -------------------- Customer Select -------------------- */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#402B69]">
              Select Customer
            </label>

            <Select
              value={selectedCustomerId}
              onValueChange={setSelectedCustomerId}
            >
              <SelectTrigger className="h-11 border-[#BBB9F3]">
                <SelectValue
                  placeholder={
                    loadingCustomers
                      ? "Loading customers..."
                      : "Choose a customer"
                  }
                />
              </SelectTrigger>

              <SelectContent>
                {/* Search */}
                <div className="p-2">
                  <input
                    type="text"
                    placeholder="Search customer..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="w-full rounded-md border border-[#DDD9F5] px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30"
                  />
                </div>

                {filteredCustomers.length === 0 && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    No customers found
                  </div>
                )}

                {filteredCustomers.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-[#402B69]">
                        {c.name}
                      </span>
                      {c.email && (
                        <span className="text-xs text-muted-foreground">
                          {c.email}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ----------------------- Form Select ----------------------- */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#402B69]">
              Select Form
            </label>

            <Select value={selectedFormId} onValueChange={setSelectedFormId}>
              <SelectTrigger className="h-11 border-[#BBB9F3]">
                <SelectValue
                  placeholder={
                    loadingForms ? "Loading forms..." : "Choose a form"
                  }
                />
              </SelectTrigger>

              <SelectContent>
                {/* Search */}
                <div className="p-2">
                  <input
                    type="text"
                    placeholder="Search form..."
                    value={formSearch}
                    onChange={(e) => setFormSearch(e.target.value)}
                    className="w-full rounded-md border border-[#DDD9F5] px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30"
                  />
                </div>

                {filteredForms.length === 0 && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    No forms found
                  </div>
                )}

                {filteredForms.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-[#BBB9F3] text-[#6E3FA9]"
          >
            Cancel
          </Button>

          <Button
            disabled={!canSend || sending}
            onClick={handleSend}
            className={
              canSend
                ? "bg-[#885ABB] hover:bg-[#6E3FA9] text-white font-semibold"
                : "bg-[#E0D7F4] text-white cursor-not-allowed"
            }
          >
            {sending
              ? ((openEncodedFormRouteInNewTab ||
                  useApiReturnedUrl ||
                  endpoint.includes("open-form") ||
                  submitLabel.toLowerCase().includes("open"))
                  ? "Opening..."
                  : "Sending...")
              : submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendFormModal;
